import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { apiOrchestrator } from 'services/apiService';

/**
 * Result interface for the batch user avatars hook
 */
export interface UseBatchUserAvatarsResult {
  /** Map of userId to imageUrl (null if not found or error) */
  avatarMap: Map<string, string | null>;
  /** Whether the batch fetch is still loading */
  isLoading: boolean;
}

/**
 * Fetches a single user avatar image URL
 *
 * @param userId - The user ID to fetch avatar for
 * @returns The image URL with cache-busting parameter or null if not found
 */
const fetchSingleAvatar = async (userId: string): Promise<string | null> => {
  try {
    const res = await apiOrchestrator.get<string | null>(
      `/users-person/get-image/${userId}`
    );

    // Backend returns pre-signed S3 URL - add cache-busting to force browser reload
    if (res.data && typeof res.data === 'string') {
      const imageUrl = new URL(res.data);
      imageUrl.searchParams.set('_cb', Date.now().toString());
      return imageUrl.toString();
    }

    return res.data || null;
  } catch {
    return null;
  }
};

/**
 * Fetches avatars for multiple users in parallel
 *
 * @param userIds - Array of unique user IDs
 * @returns Map of userId to imageUrl
 */
const fetchBatchAvatars = async (
  userIds: string[]
): Promise<Map<string, string | null>> => {
  const results = await Promise.all(
    userIds.map(async userId => {
      const imageUrl = await fetchSingleAvatar(userId);
      return { userId, imageUrl };
    })
  );

  const avatarMap = new Map<string, string | null>();
  results.forEach(({ userId, imageUrl }) => {
    avatarMap.set(userId, imageUrl);
  });

  return avatarMap;
};

/**
 * Hook to batch fetch user avatar images
 *
 * Solves the N+1 query problem by fetching all avatars in parallel
 * with a single React Query cache entry. Deduplicates userIds and
 * provides proper caching.
 *
 * @param userIds - Array of user IDs to fetch avatars for
 * @returns Object with avatarMap and loading state
 *
 * @example
 * const userIds = notifications
 *   .filter(n => isUserNotification(n))
 *   .map(n => getAvatarUserId(n, currentUserId))
 *   .filter(Boolean);
 *
 * const { avatarMap, isLoading } = useBatchUserAvatars(userIds);
 *
 * // In component:
 * const avatarUrl = avatarMap.get(userId) ?? null;
 */
export const useBatchUserAvatars = (
  userIds: (string | null | undefined)[]
): UseBatchUserAvatarsResult => {
  // Deduplicate and filter out null/undefined values
  const uniqueUserIds = useMemo(() => {
    const validIds = userIds.filter((id): id is string => !!id);
    return Array.from(new Set(validIds)).sort();
  }, [userIds]);

  // Create a stable query key based on sorted unique IDs
  const queryKey = useMemo(() => ['batchUserAvatars', uniqueUserIds.join(',')], [uniqueUserIds]);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchBatchAvatars(uniqueUserIds),
    enabled: uniqueUserIds.length > 0,
    staleTime: 1000 * 60 * 15, // 15 minutes - profile images don't change frequently
    gcTime: 1000 * 60 * 30, // 30 minutes garbage collection time
  });

  // Return empty map if no data yet or no userIds
  const avatarMap = useMemo(() => {
    if (!data) {
      return new Map<string, string | null>();
    }
    return data;
  }, [data]);

  return {
    avatarMap,
    isLoading: isLoading && uniqueUserIds.length > 0,
  };
};

export default useBatchUserAvatars;
